import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  CheckCircle2,
  XCircle,
  ShieldCheck,
  CircleSlash,
  Hourglass,
  ScanFace,
  ImageIcon,
  Search,
  AlertTriangle,
  Lock as LockIcon,
  Camera,
} from "lucide-react";
import { toast } from "sonner";

/**
 * PMC remote-verification panel.
 *
 * Rendered on the PMC Attendance page when the supervisor has declared the
 * current park "Not Visited" for the day. Instead of asking the PMC to upload
 * gallery photos for every employee, this panel surfaces each contractor
 * capture and lets the PMC click Verify or Reject. Decisions are locked once
 * submitted (admin must intervene to change).
 *
 * Props:
 *   parkId            — UUID string of the park currently shown
 *   onChange          — optional callback fired after any decision (so the parent
 *                       page can refresh other counters)
 *   onSwitchToVisited — optional callback to toggle park status to visited
 */
export function PmcRemoteVerificationPanel({ parkId, onChange, onSwitchToVisited }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busyByCap, setBusyByCap] = useState({}); // { contractor_cap_id: true }
  // Global lock: while ANY decision is being processed (or the list is
  // refreshing after one), all Verify/Reject buttons across the whole
  // panel are disabled. Fixes the Manamadurai "one click verified all"
  // report — where a PMC rapidly tapped multiple rows before the panel
  // could refresh, each tap firing an independent verify call. With
  // this guard the user must wait for the previous decision to settle
  // before another can be triggered.
  const [globalBusy, setGlobalBusy] = useState(false);
  const [search, setSearch] = useState("");
  const [lightbox, setLightbox] = useState(null); // { src, caption } | null
  const [rejectTarget, setRejectTarget] = useState(null); // contractor row
  const [rejectReason, setRejectReason] = useState("");
  const [rejectBusy, setRejectBusy] = useState(false);

  const fetchData = useCallback(async () => {
    if (!parkId) return;
    setLoading(true);
    try {
      const { data } = await api.get("/pmc/remote-verification", {
        params: { park_id: parkId },
      });
      setData(data);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Failed to load verification list";
      toast.error(typeof msg === "string" ? msg : "Failed to load");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [parkId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const rows = useMemo(() => data?.rows || [], [data]);
  const counts = data?.counts || {};

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      `${r.employee?.name || ""} ${r.employee?.code || ""}`
        .toLowerCase()
        .includes(q),
    );
  }, [rows, search]);

  // `decide` now takes the source contractor capture explicitly so we can
  // act on a specific slot (Morning vs Afternoon). The legacy callers pass
  // `row.contractor_attendance` which still represents the first/earliest
  // slot, preserving backwards compatibility.
  const decide = async (row, slotCap, action, reason) => {
    const capId = slotCap?.id;
    if (!capId) return;
    if (busyByCap?.[capId]) return;
    setBusyByCap((b) => ({ ...b, [capId]: true }));
    try {
      const res = await api.post("/pmc/remote-verification", {
        contractor_attendance_id: capId,
        action,
        reject_reason: reason || null,
      });
      toast.success(
        action === "verify"
          ? `Verified ${row.employee?.name || "employee"}${slotCap?.window_label ? " · " + slotCap.window_label : ""}`
          : `Rejected ${row.employee?.name || "employee"}${slotCap?.window_label ? " · " + slotCap.window_label : ""}`,
      );

      // Instant UI update: update local data state directly without re-fetching entire table and photos
      setData((prev) => {
        if (!prev) return prev;
        const newRows = (prev.rows || []).map((r) => {
          if (r.employee?.id !== row.employee?.id) return r;
          const newSlots = (r.slots || []).map((s) => {
            if (s.contractor_attendance?.id !== capId) return s;
            const newStatus = action === "verify" ? "marked" : "rejected";
            const newState = action === "verify" ? "verified" : "rejected";
            return {
              ...s,
              pmc_decision: {
                id: res?.data?.id,
                status: newStatus,
                rejection_reason: reason || null,
                decided_at: new Date().toISOString(),
              },
              state: newState,
            };
          });
          const states = new Set(newSlots.map((s) => s.state));
          let rowState = r.state;
          if (states.has("pending")) {
            rowState = "pending";
          } else if (states.has("rejected") && !states.has("verified")) {
            rowState = "rejected";
          } else if (states.has("verified")) {
            rowState = "verified";
          }
          const firstSlot = newSlots[0];
          return {
            ...r,
            contractor_attendance: firstSlot?.contractor_attendance || r.contractor_attendance,
            pmc_decision: firstSlot?.pmc_decision || r.pmc_decision,
            slots: newSlots,
            state: rowState,
          };
        });

        const newCounts = { verified: 0, rejected: 0, pending: 0, awaiting: 0, already_absent: 0 };
        for (const r of newRows) {
          newCounts[r.state] = (newCounts[r.state] || 0) + 1;
        }

        return {
          ...prev,
          counts: newCounts,
          rows: newRows,
        };
      });

      onChange?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : detail?.message || (e?.response?.status
              ? `Failed to record decision (HTTP ${e.response.status})`
              : "Failed to record decision"),
      );
    } finally {
      setBusyByCap((b) => {
        const n = { ...b };
        delete n[capId];
        return n;
      });
    }
  };

  const submitReject = async () => {
    const reason = rejectReason.trim();
    if (!reason) {
      toast.error("Please add a reason for rejecting this photo");
      return;
    }
    setRejectBusy(true);
    try {
      // `rejectTarget` carries the specific slot's contractor_attendance
      // payload (set when the slot's Reject button is clicked) + the
      // owning employee row, so we can pass the right capture id.
      await decide(rejectTarget.row, rejectTarget.cap, "reject", reason);
      setRejectTarget(null);
      setRejectReason("");
    } finally {
      setRejectBusy(false);
    }
  };

  if (loading && !data) {
    return (
      <Card>
        <CardContent className="p-6 text-center text-sm text-muted-foreground">
          Loading contractor captures…
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-3" data-testid="pmc-remote-verification">
      {/* Info banner */}
      <Card className="border-amber-200 bg-amber-50/60">
        <CardContent className="flex flex-wrap items-center justify-between gap-2 p-3 text-sm text-amber-900">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 shrink-0 text-amber-700" />
            <div>
              <span className="font-medium">Remote verification mode</span>
              <span className="ml-1 text-xs text-amber-800">
                — this park is marked Not Visited. Review each contractor capture
                below and Verify or Reject.
              </span>
            </div>
          </div>
          {onSwitchToVisited && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-8 border-amber-300 bg-white text-xs font-medium text-amber-900 hover:bg-amber-100 shrink-0"
              onClick={onSwitchToVisited}
              data-testid="rv-switch-to-visited-btn"
            >
              <Camera className="mr-1.5 h-3.5 w-3.5 text-emerald-600" />
              Switch to Live Camera Capture Mode
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Counts strip */}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
        <CountTile
          label="Verified"
          value={counts.verified || 0}
          tone="emerald"
          icon={CheckCircle2}
          testid="rv-count-verified"
        />
        <CountTile
          label="Rejected"
          value={counts.rejected || 0}
          tone="rose"
          icon={XCircle}
          testid="rv-count-rejected"
        />
        <CountTile
          label="Pending"
          value={counts.pending || 0}
          tone="amber"
          icon={Hourglass}
          testid="rv-count-pending"
        />
        <CountTile
          label="Awaiting capture"
          value={counts.awaiting || 0}
          tone="slate"
          icon={CircleSlash}
          testid="rv-count-awaiting"
        />
        <CountTile
          label="Already absent"
          value={counts.already_absent || 0}
          tone="zinc"
          icon={AlertTriangle}
          testid="rv-count-already-absent"
        />
      </div>

      {/* Search */}
      <Card>
        <CardContent className="flex items-center gap-2 p-2">
          <Search className="ml-1 h-4 w-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search employee by name or code…"
            className="h-9 border-0 shadow-none focus-visible:ring-0"
            data-testid="rv-search-input"
          />
        </CardContent>
      </Card>

      {/* Rows */}
      <div className="grid gap-2" data-testid="rv-rows">
        {filtered.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center text-sm text-muted-foreground">
              No employees match your search.
            </CardContent>
          </Card>
        ) : null}
        {filtered.map((row) => (
          <VerificationRow
            key={row.employee.id}
            row={row}
            busyByCap={busyByCap}
            globalBusy={globalBusy}
            onVerify={(slotCap) => decide(row, slotCap, "verify")}
            onReject={(slotCap) =>
              setRejectTarget({
                row,
                cap: slotCap,
                employee: row.employee,
              })
            }
            onZoom={(src, caption) => setLightbox({ src, caption })}
          />
        ))}
      </div>

      {/* Reject dialog */}
      <Dialog
        open={!!rejectTarget}
        onOpenChange={(v) => {
          if (!v && !rejectBusy) {
            setRejectTarget(null);
            setRejectReason("");
          }
        }}
      >
        <DialogContent className="max-w-md" data-testid="rv-reject-dialog">
          <DialogHeader>
            <DialogTitle>Reject contractor photo</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-2 text-sm">
            <div className="text-xs text-muted-foreground">
              Employee:{" "}
              <span className="font-medium text-foreground">
                {rejectTarget?.employee?.name}
              </span>{" "}
              <span className="font-mono text-[11px]">
                ({rejectTarget?.employee?.code})
              </span>
              {rejectTarget?.cap?.window_label ? (
                <>
                  {" "}·{" "}
                  <span className="font-medium text-foreground">
                    {rejectTarget.cap.window_label}
                  </span>
                </>
              ) : null}
            </div>
            <Textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Why are you rejecting this photo? (e.g., wrong person, blurry, taken elsewhere…)"
              rows={4}
              data-testid="rv-reject-reason"
            />
            <div className="rounded-md border border-amber-200 bg-amber-50 p-2 text-[11px] text-amber-900">
              <AlertTriangle className="mr-1 inline h-3 w-3" />
              This is a final decision. Admin will see this rejection.
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setRejectTarget(null);
                setRejectReason("");
              }}
              disabled={rejectBusy}
              data-testid="rv-reject-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={submitReject}
              disabled={rejectBusy || !rejectReason.trim()}
              className="bg-rose-600 text-white hover:bg-rose-700"
              data-testid="rv-reject-confirm"
            >
              {rejectBusy ? "Saving…" : "Confirm reject"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lightbox */}
      {lightbox ? (
        <Dialog open onOpenChange={() => setLightbox(null)}>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle className="text-sm">{lightbox.caption}</DialogTitle>
            </DialogHeader>
            <div className="flex max-h-[70vh] items-center justify-center overflow-auto bg-slate-900/95">
              <img
                src={lightbox.src}
                alt=""
                className="max-h-[70vh] w-auto object-contain"
              />
            </div>
          </DialogContent>
        </Dialog>
      ) : null}
    </div>
  );
}

function CountTile({ label, value, tone = "slate", icon: Icon, testid }) {
  const toneCls = {
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-800",
    rose: "border-rose-200 bg-rose-50 text-rose-800",
    amber: "border-amber-200 bg-amber-50 text-amber-800",
    slate: "border-slate-200 bg-slate-50 text-slate-700",
    zinc: "border-zinc-200 bg-zinc-50 text-zinc-700",
  }[tone];
  return (
    <div
      className={`flex items-center gap-2 rounded-md border px-3 py-2 ${toneCls}`}
      data-testid={testid}
    >
      {Icon ? <Icon className="h-4 w-4" /> : null}
      <div className="flex flex-col">
        <span className="text-[10px] uppercase tracking-wider opacity-70">
          {label}
        </span>
        <span className="text-base font-semibold tabular-nums">{value}</span>
      </div>
    </div>
  );
}

function VerificationRow({ row, busyByCap, globalBusy, onVerify, onReject, onZoom }) {
  const { employee, slots, state } = row;
  const slotList = Array.isArray(slots) && slots.length ? slots : null;
  const isAwaiting = state === "awaiting";
  const isAbsent = state === "already_absent";

  return (
    <Card data-testid={`rv-row-${employee.code}`}>
      <CardContent className="flex flex-col gap-3 p-3">
        {/* Header — employee + reference photos + row-level state badge */}
        <div className="flex flex-wrap items-start gap-3 sm:items-center">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-medium">{employee.name}</span>
              <span className="font-mono text-[11px] text-muted-foreground">
                {employee.code}
              </span>
              <StateBadge state={state} />
              {slotList && slotList.length > 1 ? (
                <Badge
                  variant="outline"
                  className="border-indigo-300 bg-indigo-50 text-indigo-800"
                  data-testid={`rv-row-${employee.code}-slot-count`}
                >
                  {slotList.length} slots
                </Badge>
              ) : null}
            </div>
            {isAwaiting ? (
              <div className="mt-1 text-[11px] text-muted-foreground">
                <ImageIcon className="mr-1 inline h-3 w-3" />
                Awaiting contractor capture for today.
              </div>
            ) : null}
            {isAbsent ? (
              <div className="mt-1 text-[11px] text-muted-foreground">
                Already marked Absent — no verification needed.
              </div>
            ) : null}
          </div>

          {/* Reference photos — last 3 accepted captures from prior days */}
          {Array.isArray(row.recent_photos) && row.recent_photos.length > 0 ? (
            <div
              className="flex shrink-0 flex-col items-end gap-1"
              data-testid={`rv-row-${employee.code}-refs`}
            >
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                Reference
              </div>
              <div className="flex gap-1">
                {row.recent_photos.map((rp) => {
                  const src = rp.photo_b64?.startsWith("data:")
                    ? rp.photo_b64
                    : `data:image/jpeg;base64,${rp.photo_b64 || ""}`;
                  return (
                    <button
                      key={rp.id}
                      type="button"
                      onClick={() =>
                        onZoom?.(
                          src,
                          `${employee.name} (${employee.code}) — reference ${rp.date || ""}`,
                        )
                      }
                      className="h-12 w-12 overflow-hidden rounded border bg-slate-900/95"
                      data-testid={`rv-row-${employee.code}-ref-${rp.id}`}
                      title={rp.date || ""}
                    >
                      <img src={src} alt="" className="h-full w-full object-cover" />
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}
        </div>

        {/* Slot cards — one per contractor capture (Morning, Afternoon, …) */}
        {slotList ? (
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {slotList.map((slot) => (
              <SlotCard
                key={slot.contractor_attendance.id}
                employee={employee}
                slot={slot}
                busy={!!busyByCap?.[slot.contractor_attendance.id] || !!globalBusy}
                onVerify={() => onVerify?.(slot.contractor_attendance)}
                onReject={() => onReject?.(slot.contractor_attendance)}
                onZoom={onZoom}
              />
            ))}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

function SlotCard({ employee, slot, busy, onVerify, onReject, onZoom }) {
  const cap = slot.contractor_attendance;
  const dec = slot.pmc_decision;
  const s = slot.state;
  const isVerified = s === "verified";
  const isRejected = s === "rejected";
  const isPending = s === "pending";
  const photoSrc = cap?.photo_b64
    ? cap.photo_b64.startsWith("data:")
      ? cap.photo_b64
      : `data:image/jpeg;base64,${cap.photo_b64}`
    : null;
  const label = cap?.window_label || "Open";
  const tone = isVerified
    ? "border-emerald-300 bg-emerald-50/60"
    : isRejected
      ? "border-rose-300 bg-rose-50/60"
      : "border-amber-300 bg-amber-50/40";

  return (
    <div
      className={`rounded-md border ${tone} p-2`}
      data-testid={`rv-slot-${employee.code}-${cap.window_id || "open"}`}
    >
      <div className="flex items-start gap-2">
        <button
          type="button"
          onClick={() =>
            photoSrc
              ? onZoom?.(
                  photoSrc,
                  `${employee.name} (${employee.code}) · ${label} — by ${cap?.captured_by_name || "—"}`,
                )
              : null
          }
          disabled={!photoSrc}
          className="grid h-16 w-16 shrink-0 place-items-center overflow-hidden rounded border bg-slate-900/95"
          data-testid={`rv-slot-${employee.code}-${cap.window_id || "open"}-photo`}
          aria-label="Open contractor photo"
        >
          {photoSrc ? (
            <img src={photoSrc} alt="" className="h-full w-full object-cover" />
          ) : (
            <ScanFace className="h-5 w-5 opacity-60" />
          )}
        </button>
        <div className="min-w-0 flex-1 text-xs">
          <div className="flex flex-wrap items-center gap-1.5">
            <Badge variant="outline" className="border-slate-300">
              {label}
            </Badge>
            <SlotStateBadge state={s} />
          </div>
          <div className="mt-1 truncate text-[11px] text-muted-foreground">
            {cap?.captured_at
              ? new Date(cap.captured_at).toLocaleTimeString("en-IN", {
                  timeZone: "Asia/Kolkata",
                  hour: "2-digit",
                  minute: "2-digit",
                })
              : "—"}
            {cap?.captured_by_name ? <> · {cap.captured_by_name}</> : null}
          </div>
          {isRejected && dec?.rejection_reason ? (
            <div
              className="mt-1 rounded border border-rose-200 bg-white/70 p-1 text-[11px] text-rose-800"
              data-testid={`rv-slot-${employee.code}-${cap.window_id || "open"}-reason`}
            >
              <span className="font-medium">Reason:</span> {dec.rejection_reason}
            </div>
          ) : null}
        </div>
      </div>

      <div className="mt-2 flex items-center justify-end gap-1.5">
        {isPending ? (
          <>
            <Button
              size="sm"
              variant="outline"
              className="h-8 text-[11px] text-rose-700 hover:bg-rose-50"
              onClick={onReject}
              disabled={busy}
              data-testid={`rv-slot-${employee.code}-${cap.window_id || "open"}-reject`}
            >
              <XCircle className="mr-1 h-3.5 w-3.5" />
              Reject
            </Button>
            <Button
              size="sm"
              className="h-8 bg-emerald-600 text-[11px] text-white hover:bg-emerald-700"
              onClick={onVerify}
              disabled={busy}
              data-testid={`rv-slot-${employee.code}-${cap.window_id || "open"}-verify`}
            >
              <CheckCircle2 className="mr-1 h-3.5 w-3.5" />
              {busy ? "Saving…" : "Verify"}
            </Button>
          </>
        ) : (
          <Badge
            variant="outline"
            className="border-slate-300 text-slate-600"
            data-testid={`rv-slot-${employee.code}-${cap.window_id || "open"}-locked`}
          >
            <LockIcon className="mr-1 h-3 w-3" /> Locked
          </Badge>
        )}
      </div>
    </div>
  );
}

function SlotStateBadge({ state }) {
  if (state === "verified")
    return (
      <Badge className="h-5 bg-emerald-600 text-[10px] text-white hover:bg-emerald-600">
        <CheckCircle2 className="mr-0.5 h-3 w-3" /> Verified
      </Badge>
    );
  if (state === "rejected")
    return (
      <Badge className="h-5 bg-rose-600 text-[10px] text-white hover:bg-rose-600">
        <XCircle className="mr-0.5 h-3 w-3" /> Rejected
      </Badge>
    );
  return (
    <Badge variant="outline" className="h-5 border-amber-300 bg-amber-50 text-[10px] text-amber-800">
      <Hourglass className="mr-0.5 h-3 w-3" /> Pending
    </Badge>
  );
}

function StateBadge({ state }) {
  if (state === "verified") {
    return (
      <Badge className="bg-emerald-600 text-white hover:bg-emerald-600">
        <CheckCircle2 className="mr-1 h-3 w-3" /> Verified
      </Badge>
    );
  }
  if (state === "rejected") {
    return (
      <Badge className="bg-rose-600 text-white hover:bg-rose-600">
        <XCircle className="mr-1 h-3 w-3" /> Rejected
      </Badge>
    );
  }
  if (state === "pending") {
    return (
      <Badge
        variant="outline"
        className="border-amber-300 bg-amber-50 text-amber-800"
      >
        <Hourglass className="mr-1 h-3 w-3" /> Pending review
      </Badge>
    );
  }
  if (state === "already_absent") {
    return (
      <Badge variant="outline" className="border-rose-300 bg-rose-50 text-rose-700">
        Absent
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="border-slate-300 text-slate-600">
      <CircleSlash className="mr-1 h-3 w-3" /> Awaiting capture
    </Badge>
  );
}
